import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import './ExamDetails.css'
import examsData from './examsData.json';
import examsDataMarathi from './examsDataMarathi.json';
import TestSeries from './TestSeries';
import SEO from '../common/SEO';
import DownloadCard from './DownloadCard';
import axiosInstance from '../../api/axiosInstance';
import { Spinner } from 'react-bootstrap';
import { slug as slugify } from 'github-slugger';

function importAll(r) {
  let images = {};
  r.keys().forEach((item) => {
    images[item.replace('./', '')] = r(item);
  });
  return images;
}
const images = importAll(require.context('../../assets/test-series-image/', false, /\.(png|jpe?g|svg|webp)$/));

const TestSeriesSection = ({ tests, language }) => {
  return (
    <div className="test-series-grid">
      {tests.map((paper) => (
        <div key={paper.id} className="test-series-card">
          <div className="test-series-content">
            <h3>{paper.title}</h3>
            <div className="test-series-meta">
              <span className="test-duration">
                <i className="fas fa-clock"></i> {paper.duration}
              </span>
              <span className="test-marks">
                <i className="fas fa-star"></i> {paper.marks} {language === 'mr' ? 'गुण' : 'Marks'}
              </span>
              <span className="test-questions">
                <i className="fas fa-question"></i> {paper.questions} {language === 'mr' ? 'प्रश्न' : 'Questions'}
              </span>
            </div>
          </div>
          <div className="test-series-action">
            <div className="test-series-price">{language === 'mr' ? 'मोफत' : 'Free'}</div>
            <Link to={paper.link} className="test-series-button">
              {language === 'mr' ? 'पेपर पहा' : 'View Paper'}
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
};

let cachedAllTests = null;
let cachedTestsPromise = null;

const getCachedTests = async () => {
  if (cachedAllTests) return cachedAllTests;
  if (cachedTestsPromise) return cachedTestsPromise;

  cachedTestsPromise = axiosInstance.get('/tests', {
    params: {
      page: 1,
      limit: 0,
      paginated: false
    }
  }).then(response => {
    if (response.data && response.data.tests) {
      cachedAllTests = response.data.tests;
    }
    cachedTestsPromise = null;
    return cachedAllTests || [];
  }).catch(err => {
    cachedTestsPromise = null;
    throw err;
  });

  return cachedTestsPromise;
};

const ExamDetails = () => {
  const { examSlug } = useParams();
  const tabOptions = ['overview', 'eligibility', 'syllabus', 'cutoff', 'applicationfees', 'testseries'];

  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined' || !examSlug) return 'overview';
    try {
      const savedTab = window.localStorage.getItem(`examDetailsActiveTab:${examSlug}`);
      if (savedTab && tabOptions.includes(savedTab)) {
        return savedTab;
      }
    } catch (error) {
      console.warn('Unable to read saved tab from localStorage:', error);
    }
    return 'overview';
  });

  const [language, setLanguage] = useState('mr');
  const [subjectTests, setSubjectTests] = useState([]);
  const [pyqTestsList, setPyqTestsList] = useState([]);
  const [prelimMockTests, setPrelimMockTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(true);

  const examData = examsData.exams.find(exam => exam.slug === examSlug);

  useEffect(() => {
    if (!examSlug) return;
    try {
      window.localStorage.setItem(`examDetailsActiveTab:${examSlug}`, activeTab);
    } catch (error) {
      console.warn('Unable to save active tab:', error);
    }
  }, [activeTab, examSlug]);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        setLoadingTests(true);
        const allTests = await getCachedTests();

        if (allTests && Array.isArray(allTests)) {
          const subjectCats = [
            'history', 'geography', 'economics', 'polity', 'science',
            'general science', 'arithmetic', 'mathematics', 'logical reasoning',
            'reasoning', 'current affairs', 'general knowledge'
          ];

          const pyqs = [];
          const subjectWise = [];
          const mocks = [];

          allTests.forEach(test => {
            const testNameLower = test.testName.toLowerCase();
            const categoryLower = test.category ? test.category.toLowerCase() : '';
            const testSeriesLower = test.testSeries ? test.testSeries.toLowerCase() : '';

            const matchesMpscKeyword =
              testNameLower.includes('mpsc') ||
              testNameLower.includes('गट') ||
              testNameLower.includes('संयुक्त') ||
              testNameLower.includes('पूर्व परीक्षा') ||
              testNameLower.includes('राज्यसेवा') ||
              testNameLower.includes('maharashtra') ||
              testNameLower.includes('महाराष्ट्र') ||
              categoryLower.includes('mpsc') ||
              testSeriesLower.includes('mpsc');

            const isSubjectCat = subjectCats.includes(categoryLower);

            // Exclude tests branded for other competitive exams
            const isBrandedForOther =
              testNameLower.includes('upsc') ||
              testNameLower.includes('ssc') ||
              testNameLower.includes('banking') ||
              testNameLower.includes('rrb') ||
              testNameLower.includes('railway') ||
              testNameLower.includes('ibps') ||
              testNameLower.includes('police') ||
              testNameLower.includes('पोलीस') ||
              categoryLower.includes('upsc') ||
              categoryLower.includes('ssc') ||
              categoryLower.includes('banking') ||
              categoryLower.includes('rrb') ||
              categoryLower.includes('police') ||
              categoryLower.includes('पोलीस');

            if (!isBrandedForOther) {
              const hasDevanagari = /[\u0900-\u097F]/.test(test.testName);

              const shouldInclude = matchesMpscKeyword || (
                isSubjectCat && (
                  categoryLower === 'general knowledge' ||
                  categoryLower === 'current affairs' ||
                  categoryLower === 'general aptitude test' ||
                  hasDevanagari
                )
              );

              if (shouldInclude) {
                const title = (language === 'mr' && test.testNameMarathi && test.testNameMarathi.marathi)
                  ? test.testNameMarathi.marathi
                  : test.testName;

                const slug = slugify(test.testName || '').substring(0, 60) || 'test';

                const formattedItem = {
                  id: test._id,
                  title: title,
                  duration: `${test.totalMinutes} ${language === 'mr' ? 'मिनिटे' : 'mins'}`,
                  marks: test.totalMarks,
                  questions: test.questions ? test.questions.length : 0,
                  link: `/test/${slug}/${test._id}`,
                  isPreviousYearPaper: test.isPreviousYearPaper || false
                };

                const hasYear = /\b(201\d|202\d)\b/.test(test.testName);
                const isPyq = test.isPreviousYearPaper === true ||
                  (hasYear && (testNameLower.includes('paper') || testNameLower.includes('परीक्षा') || testNameLower.includes('prelim')));

                if (matchesMpscKeyword) {
                  if (isPyq) {
                    pyqs.push(formattedItem);
                  } else if (isSubjectCat && !testNameLower.includes('prelim') && !testNameLower.includes('पूर्व परीक्षा') && !testNameLower.includes('mock')) {
                    subjectWise.push(formattedItem);
                  } else {
                    mocks.push(formattedItem);
                  }
                } else if (isSubjectCat) {
                  subjectWise.push(formattedItem);
                }
              }
            }
          });

          setPyqTestsList(pyqs);
          setSubjectTests(subjectWise.slice(0, 9));
          setPrelimMockTests(mocks);
        }
      } catch (error) {
        console.error('Error fetching tests:', error);
      } finally {
        setLoadingTests(false);
      }
    };

    if (examData && examData.category && examData.category.toLowerCase() === 'mpsc') {
      fetchTests();
    } else {
      setLoadingTests(false);
    }
  }, [examData?.slug, language]);

  const getField = (fieldName) => {
    if (!examData) return '';
    const marathiExamData = examsDataMarathi[examData.slug];
    if (language === 'mr' && marathiExamData) {
      if (fieldName.includes('.')) {
        const parts = fieldName.split('.');
        let val = marathiExamData;
        for (let part of parts) {
          val = val ? val[part] : undefined;
        }
        if (val !== undefined) return val;
      } else if (marathiExamData[fieldName] !== undefined) {
        return marathiExamData[fieldName];
      }
    }

    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      let val = examData;
      for (let part of parts) {
        val = val ? val[part] : undefined;
      }
      return val;
    }
    return examData[fieldName];
  };

  const getTabLabel = (tabKey) => {
    const labels = {
      overview: { en: 'Overview', mr: 'माहिती' },
      eligibility: { en: 'Eligibility', mr: 'पात्रता' },
      syllabus: { en: 'Syllabus & Pattern', mr: 'अभ्यासक्रम आणि परीक्षा पद्धत' },
      cutoff: { en: 'Cutoff', mr: 'कटऑफ' },
      applicationfees: { en: 'Application Fees', mr: 'परीक्षा शुल्क' },
      testseries: { en: 'Test Series', mr: 'टेस्ट सिरीज' }
    };
    return labels[tabKey] ? labels[tabKey][language] : tabKey;
  };

  const generateKeywords = () => {
    if (!examData) return '';
    const baseKeywords = [
      examData.title,
      examData.conductingBody,
      examData.slug,
      examData.category,
      'application fees',
      `${examData.conductingBody} exam`,
      `${examData.title} preparation`,
      `${examData.title} test series`,
      ` ${examData.title} syllabus`,
      `${examData.title} pattern`,
      `${examData.title} eligibility`,
      `${examData.title} cutoff`
    ];

    if (examData.syllabus && examData.syllabus.table) {
      examData.syllabus.table.forEach(subject => {
        baseKeywords.push(subject.subject);
      });
    }

    return baseKeywords.join(', ');
  };

  const renderHTML = (string) => {
    return { __html: string };
  };

  return (
    <div className="container mt-3 exam-container">

      <SEO
        title={examData.title}
        description={examData.overview.replace(/<[^>]*>/g, '').substring(0, 160)}
        keywords={generateKeywords()}
        image={images[examData.image]}
        url={`/exams-details/${examSlug}`}
        type="article"
        publishedTime={'2025-09-09T00:00:00Z'}
        section="Exam Details"
        tags={[examData.title, examData.conductingBody, 'exam preparation', 'test series']}
        schema={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": examData.title,
          "description": examData.overview.replace(/<[^>]*>/g, '').substring(0, 200),
          "provider": {
            "@type": "Organization",
            "name": "My Exam World",
            "sameAs": "https://www.myexamworld.com"
          }
        }}
      />

      {/* Breadcrumb Navigation with Schema.org markup */}
      <nav aria-label="Breadcrumb" className="exam-details-breadcrumb">
        <div className="breadcrumb-inner" itemScope itemType="https://schema.org/BreadcrumbList">
          <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <Link itemProp="item" to="/">
              <span itemProp="name">Home</span>
            </Link>
            <meta itemProp="position" content="1" />
          </span>
          &gt;
          <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <Link itemProp="item" to="/exams-details">
              <span itemProp="name">Exams</span>
            </Link>
            <meta itemProp="position" content="2" />
          </span>
          &gt;
          <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <span itemProp="name">{examData.title}</span>
            <meta itemProp="position" content="3" />
          </span>
        </div>
      </nav>


      {/* Exam Header */}
      <div className="exam-details-header">
        <div className="exam-details-icon">
          <img src={images[examData.image]} alt="No Image" style={{ width: 60, height: 60, objectFit: "contain" }} />
        </div>
        <div className="exam-details-title">
          <h1>{getField('title')}</h1>
          <p>{language === 'mr' ? 'आयोजक:' : 'Conducted by:'} {getField('conductingBody')}</p>
        </div>
        {examsDataMarathi[examData.slug] && (
          <div className="exam-details-lang-toggle">
            <button
              className={`lang-btn ${language === 'mr' ? 'active' : ''}`}
              onClick={() => setLanguage('mr')}
            >
              मराठी
            </button>
            <button
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              English
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="exam-details-tabs-container">
        <div className="exam-details-tabs">
          <button
            className={`exam-details-tab ${activeTab === 'overview' ? 'exam-details-tab-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            {getTabLabel('overview')}
          </button>
          <button
            className={`exam-details-tab ${activeTab === 'eligibility' ? 'exam-details-tab-active' : ''}`}
            onClick={() => setActiveTab('eligibility')}
          >
            {getTabLabel('eligibility')}
          </button>
          <button
            className={`exam-details-tab ${activeTab === 'syllabus' ? 'exam-details-tab-active' : ''}`}
            onClick={() => setActiveTab('syllabus')}
          >
            {getTabLabel('syllabus')}
          </button>
          <button
            className={`exam-details-tab ${activeTab === 'cutoff' ? 'exam-details-tab-active' : ''}`}
            onClick={() => setActiveTab('cutoff')}
          >
            {getTabLabel('cutoff')}
          </button>
          <button
            className={`exam-details-tab ${activeTab === 'applicationfees' ? 'exam-details-tab-active' : ''}`}
            onClick={() => setActiveTab('applicationfees')}
          >
            {getTabLabel('applicationfees')}
          </button>
          <button
            className={`exam-details-tab ${activeTab === 'testseries' ? 'exam-details-tab-active' : ''}`}
            onClick={() => setActiveTab('testseries')}
          >
            {getTabLabel('testseries')}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="exam-details-tab-content">
        {/* MPSC Subject-Wise Banner (Visible on ALL tabs for MPSC) */}
        {examData && examData.category && examData.category.toLowerCase() === 'mpsc' && (
          <div 
            className="p-3 mb-4 rounded-3 shadow-sm border border-primary bg-light d-flex flex-column flex-sm-row align-items-center justify-content-between cursor-pointer"
            style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #e3f2fd, #ffffff)' }}
            onClick={() => window.location.href = '/subject-wise-series/mpsc'}
          >
            <div className="mb-2 mb-sm-0">
              <span className="badge bg-primary mb-1">MPSC Special</span>
              <h5 className="fw-bold text-dark mb-0">MPSC Subject & Topic Wise Test Series</h5>
              <small className="text-muted">Prepare MPSC Polity, History, Geography & More topic-by-topic</small>
            </div>
            <Link to="/subject-wise-series/mpsc" className="btn button-design btn-sm px-3 text-nowrap fw-semibold">
              Explore Subject Tests →
            </Link>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="exam-details-tab-panel">
            <h2>{language === 'mr' ? 'परीक्षेची माहिती' : 'Exam Overview'}</h2>
            <p dangerouslySetInnerHTML={renderHTML(getField('overview'))} />

            {examData.importantUpdates && (
              <div className="exam-details-updates-card">
                <div className="updates-card-header">
                  <span className="updates-badge">{language === 'mr' ? 'नवीन अपडेट' : 'Latest Update'}</span>
                  <h3>{language === 'mr' ? 'जुलै २०२६ शुद्धिपत्रक आणि महत्त्वाचे बदल' : 'July 2026 Corrigendum & Key Notifications'}</h3>
                </div>
                <div className="updates-grid">
                  <div className="updates-grid-item">
                    <span className="updates-label">{language === 'mr' ? 'एकूण रिक्त पदे' : 'Total Vacancies'}</span>
                    <span className="updates-value">{getField('importantUpdates.vacancies')}</span>
                  </div>
                  <div className="updates-grid-item">
                    <span className="updates-label">{language === 'mr' ? 'परीक्षा पद्धत' : 'Exam Mode'}</span>
                    <span className="updates-value">{getField('modeofexam')}</span>
                  </div>
                  <div className="updates-grid-item">
                    <span className="updates-label">{language === 'mr' ? 'पूर्व परीक्षा तारीख' : 'Prelims Exam Date'}</span>
                    <span className="updates-value">{getField('importantUpdates.examDates.prelims')}</span>
                  </div>
                  <div className="updates-grid-item">
                    <span className="updates-label">{language === 'mr' ? 'मुख्य परीक्षा तारीख' : 'Mains Exam Date'}</span>
                    <span className="updates-value">{getField('importantUpdates.examDates.mains')}</span>
                  </div>
                </div>
                <div className="updates-deadlines">
                  <h4>{language === 'mr' ? 'महत्त्वाच्या मुदती' : 'Important Deadlines'}</h4>
                  <ul>
                    <li><strong>{language === 'mr' ? 'अर्ज करण्याची अंतिम तारीख:' : 'Last Date to Apply:'}</strong> {getField('importantUpdates.deadlines.applyLastDate')}</li>
                    <li><strong>{language === 'mr' ? 'ऑनलाईन शुल्क भरण्याची अंतिम तारीख:' : 'Online Fee Payment Last Date:'}</strong> {getField('importantUpdates.deadlines.feePaymentLastDate')}</li>
                    {getField('importantUpdates.deadlines.centerSelection') && (
                      <li><strong>{language === 'mr' ? 'जिल्हा परीक्षा केंद्र निवड:' : 'District Center Selection:'}</strong> {getField('importantUpdates.deadlines.centerSelection')}</li>
                    )}
                  </ul>
                </div>
                <div className="updates-highlights-list">
                  <h4>{language === 'mr' ? 'शुद्धिपत्रक ठळक मुद्दे' : 'Corrigendum Highlights'}</h4>
                  <ul>
                    {getField('importantUpdates.highlights') && getField('importantUpdates.highlights').map((highlight, idx) => (
                      <li key={idx} dangerouslySetInnerHTML={renderHTML(highlight)} />
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'applicationfees' && (
          <div className="exam-details-tab-panel">
            <h2>{language === 'mr' ? 'परीक्षा शुल्क' : 'Application Fees'}</h2>
            <ul>
              <li><strong>{language === 'mr' ? 'खुला प्रवर्ग (General):' : 'General Category:'}</strong> {getField('applicationfees.general')}</li>
              <li><strong>{language === 'mr' ? 'राखीव प्रवर्ग (SC/ST/OBC):' : 'Reserved Category (SC/ST/OBC):'}</strong> {getField('applicationfees.reserved')}</li>
            </ul>
            <p>{language === 'mr' ? '(परीक्षा शुल्कात बदल होऊ शकतो, नेहमी अधिकृत जाहिरात तपासावी)' : '(Fees may vary, always check the official notification)'}</p>
          </div>
        )}

        {activeTab === 'syllabus' && (
          <div className="exam-details-tab-panel">
            <h2>{language === 'mr' ? 'अभ्यासक्रम आणि परीक्षा पद्धत' : 'Syllabus & Exam Pattern'}</h2>
            <p dangerouslySetInnerHTML={renderHTML(getField('syllabus.intro'))} />

            <div className="exam-details-overview">
              <h3>{language === 'mr' ? 'परीक्षा पद्धत' : 'Exam Pattern'}</h3>
              <ul>
                <li><strong>{language === 'mr' ? 'परीक्षा पद्धत:' : 'Mode of Exam:'}</strong> {getField('modeofexam')}</li>
                <li><strong>{language === 'mr' ? 'प्रश्नांचे स्वरूप:' : 'Type of Questions:'}</strong> {getField('typeofquestions')}</li>
                {examData.totalQuestions && <li><strong>{language === 'mr' ? 'एकूण प्रश्न:' : 'Total Questions:'}</strong> {examData.totalQuestions}</li>}
                <li><strong>{language === 'mr' ? 'एकूण गुण:' : 'Total Marks:'}</strong> {getField('totlamarks')}</li>
                <li><strong>{language === 'mr' ? 'कालावधी:' : 'Duration:'}</strong> {getField('duration')}</li>
                <li><strong>{language === 'mr' ? 'नकारात्मक गुण पद्धत (Negative Marking):' : 'Negative Marking:'}</strong> {getField('negativemarking')} </li>
              </ul>
            </div>

            {examData.syllabus.table && examData.syllabus.table.length > 0 && (
              <div className="exam-details-syllabus-section">
                <div className="exam-details-syllabus-table">
                  <div className="exam-details-table-header">
                    <div className="exam-details-table-cell">Subject</div>
                    <div className="exam-details-table-cell">Questions</div>
                    <div className="exam-details-table-cell">Marks</div>
                  </div>
                  {examData.syllabus.table.map((subject, index) => (
                    <div className="exam-details-table-row" key={index}>
                      <div className="exam-details-table-cell" data-label="Subject">{subject.subject}</div>
                      <div className="exam-details-table-cell" data-label="Questions">{subject.questions}</div>
                      <div className="exam-details-table-cell" data-label="Marks">{subject.marks}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {examData.syllabus.syllabusDownloadUrl && (
              <DownloadCard
                title={language === 'mr' ? 'MPSC गट-क पूर्व परीक्षा अभ्यासक्रम PDF' : 'MPSC Group-C Prelims Syllabus PDF'}
                description={language === 'mr' ? 'नवीन बदलानुसार सुधारित अभ्यासक्रमाची PDF मिळवण्यासाठी बटणावर क्लिक करा.' : 'Click to download the syllabus PDF based on the latest MPSC pattern:'}
                buttonText={language === 'mr' ? 'डाऊनलोड करा' : 'Download PDF'}
                link={examData.syllabus.syllabusDownloadUrl}
              />
            )}
          </div>
        )}

        {activeTab === 'cutoff' && (
          <div className="exam-details-tab-panel">
            <h2>{language === 'mr' ? 'मागील वर्षांचे कटऑफ' : 'Previous Year Cutoffs'}</h2>

            <div className="exam-details-cutoff-table">
              <div className="exam-details-table-header">
                <div className="exam-details-table-cell">{language === 'mr' ? 'वर्ष' : 'Year'}</div>
                <div className="exam-details-table-cell">{language === 'mr' ? 'खुला (General)' : 'General'}</div>
                <div className="exam-details-table-cell">{language === 'mr' ? 'ओबीसी (OBC)' : 'OBC'}</div>
                <div className="exam-details-table-cell">{language === 'mr' ? 'एससी (SC)' : 'SC'}</div>
                <div className="exam-details-table-cell">{language === 'mr' ? 'एसटी (ST)' : 'ST'}</div>
              </div>
              {examData.cutoffs.map((cutoff, index) => (
                <div className="exam-details-table-row" key={index}>
                  <div className="exam-details-table-cell" data-label="Year">{cutoff.year}</div>
                  <div className="exam-details-table-cell" data-label="General">{cutoff.general}</div>
                  <div className="exam-details-table-cell" data-label="OBC">{cutoff.obc}</div>
                  <div className="exam-details-table-cell" data-label="SC">{cutoff.sc}</div>
                  <div className="exam-details-table-cell" data-label="ST">{cutoff.st}</div>
                </div>
              ))}
            </div>

            <div className="exam-details-cutoff-notes">
              <h3>{language === 'mr' ? 'महत्त्वाच्या नोंदी:' : 'Important Notes:'}</h3>
              <ul>
                <li>{language === 'mr' ? 'हे कटऑफ केवळ सूचक/अंदाजे आहेत आणि जिल्हा, रिक्त जागा आणि परीक्षेच्या काठिण्य पातळीनुसार बदलू शकतात. अचूक कटऑफसाठी नेहमी अधिकृत पोर्टलचा संदर्भ घ्यावा।' : 'These cutoffs are indicative/approximate and may vary depending on district, number of vacancies, and difficulty level of the exam. Always refer to the official Portal for exact cutoffs.'}</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'testseries' && (
          <div className="tab-panel">
            {examData && examData.category && examData.category.toLowerCase() === 'mpsc' ? (
              loadingTests ? (
                <>
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '180px' }}>
                    <Spinner animation="border" className="main-color" />
                  </div>
                  {examData.pyqPdfs && examData.pyqPdfs.length > 0 && (
                    <div className="test-section-group mt-4">
                      <div className="test-series-header">
                        <h2>{language === 'mr' ? 'मागील वर्षांचे पेपर्स (PDF डाऊनलोड)' : 'Previous Year Papers (PDF Download)'}</h2>
                        <p>{language === 'mr' ? 'अभ्यास करण्यासाठी मागील वर्षांच्या प्रश्नपत्रिका PDF स्वरूपात डाऊनलोड करा:' : 'Download previous year question papers in PDF format for offline study:'}</p>
                      </div>
                      <div className="download-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
                        {examData.pyqPdfs.map((pdf, idx) => (
                          <DownloadCard
                            key={idx}
                            title={language === 'mr' && pdf.titleMarathi ? pdf.titleMarathi : pdf.title}
                            description={language === 'mr' && pdf.descriptionMarathi ? pdf.descriptionMarathi : pdf.description}
                            buttonText={language === 'mr' ? 'प्रश्नपत्रिका' : 'Question Paper'}
                            link={pdf.link}
                            answerKeyLink={pdf.answerKeyLink}
                            answerKeyText={pdf.answerKeyLink === 'coming_soon' ? (language === 'mr' ? 'लवकरच उपलब्ध' : 'Available Soon') : (language === 'mr' ? 'उत्तरतालिका' : 'Answer Key')}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="exam-details-test-sections">
                  {/* Section 1: Prelims Mock Tests */}
                  {prelimMockTests.length > 0 && (
                    <div className="test-section-group">
                      <div className="test-series-header">
                        <h2>{language === 'mr' ? 'पूर्व परीक्षा मॉक टेस्ट सिरीज' : 'Prelims Mock Test Series'}</h2>
                        <p>{language === 'mr' ? 'नवीन अभ्यासक्रमावर आधारित पूर्व परीक्षा मॉक टेस्ट सिरीज:' : 'Full length mock test series based on the latest pattern:'}</p>
                      </div>
                      <TestSeriesSection tests={prelimMockTests} language={language} />
                    </div>
                  )}

                  {/* Section 2: Previous Year Papers */}
                  {pyqTestsList.length > 0 && (
                    <div className="test-section-group mt-4">
                      <div className="test-series-header">
                        <h2>{language === 'mr' ? 'मागील वर्षांचे पेपर्स (ऑनलाईन सराव)' : 'Previous Year Papers (Online Practice)'}</h2>
                        <p>{language === 'mr' ? 'मागील वर्षांच्या मूळ प्रश्नपत्रिका ऑनलाईन सोडवून सराव करा:' : 'Practice by solving actual previous year papers online:'}</p>
                      </div>
                      <TestSeriesSection tests={pyqTestsList} language={language} />
                    </div>
                  )}



                  {/* Section 3: Subject-Wise Tests */}
                  {subjectTests.length > 0 && (
                    <div className="test-section-group mt-4">
                      <div className="test-series-header">
                        <h2>{language === 'mr' ? 'विषयनिहाय टेस्ट सिरीज' : 'Subject-Wise Test Series'}</h2>
                        <p>{language === 'mr' ? 'प्रत्येक विषयाच्या घटकांनुसार सराव करा:' : 'Strengthen your core subjects with topic-wise tests:'}</p>
                      </div>
                      <TestSeriesSection tests={subjectTests} language={language} />
                    </div>
                  )}

                  {prelimMockTests.length === 0 && pyqTestsList.length === 0 && subjectTests.length === 0 && (
                    <div className="no-tests-message">
                      <p>{language === 'mr' ? 'या परीक्षेसाठी अजून कोणतीही टेस्ट उपलब्ध नाही.' : 'No tests available for this exam yet.'}</p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <TestSeries tests={examData.testseries || []} examTitle={getField('title')} language={language} />
            )}
            <div className="exam-details-view-all">
              <Link to="/alltest" className="exam-details-view-all-link">
                {language === 'mr' ? 'सर्व टेस्ट सिरीज पहा' : 'View All Test Series'} <i className="fas fa-arrow-right"></i>
              </Link>
            </div>

            {examData.pyqPdfs && examData.pyqPdfs.length > 0 && (
              <div className="test-section-group mt-4">
                <div className="test-series-header">
                  <h2>{language === 'mr' ? 'मागील वर्षांचे पेपर्स (PDF डाऊनलोड)' : 'Previous Year Papers (PDF Download)'}</h2>
                  <p>{language === 'mr' ? 'अभ्यास करण्यासाठी मागील वर्षांच्या प्रश्नपत्रिका PDF स्वरूपात डाऊनलोड करा:' : 'Download previous year question papers in PDF format for offline study:'}</p>
                </div>
                <div className="download-cards-container" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}>
                  {examData.pyqPdfs.map((pdf, idx) => (
                    <DownloadCard
                      key={idx}
                      title={language === 'mr' && pdf.titleMarathi ? pdf.titleMarathi : pdf.title}
                      description={language === 'mr' && pdf.descriptionMarathi ? pdf.descriptionMarathi : pdf.description}
                      buttonText={language === 'mr' ? 'प्रश्नपत्रिका' : 'Question Paper'}
                      link={pdf.link}
                      answerKeyLink={pdf.answerKeyLink}
                      answerKeyText={pdf.answerKeyLink === 'coming_soon' ? (language === 'mr' ? 'लवकरच उपलब्ध' : 'Available Soon') : (language === 'mr' ? 'उत्तरतालिका' : 'Answer Key')}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'eligibility' && (
          <div className="exam-details-tab-panel">
            <h2>{language === 'mr' ? 'पात्रता निकष' : 'Eligibility Criteria'}</h2>

            <div className="exam-details-eligibility-section">
              <h3>{language === 'mr' ? 'राष्ट्रीयत्व' : 'Nationality'}</h3>
              <p>{getField('eligibilityCriteria.nationality')}</p>
            </div>

            <div className="exam-details-eligibility-section">
              <h3>{language === 'mr' ? 'वयोमर्यादा' : 'Age Limit'}</h3>
              <p>{getField('eligibilityCriteria.age')}</p>
            </div>

            <div className="exam-details-eligibility-section">
              <h3>{language === 'mr' ? 'शैक्षणिक पात्रता' : 'Educational Qualification'}</h3>
              <p>{getField('eligibilityCriteria.education')}</p>
            </div>
            {examData?.eligibilityCriteria?.physical && <div className="exam-details-eligibility-section">
              <h3>{language === 'mr' ? 'शारीरिक निकष' : 'Physical Standards'}</h3>
              <div className="exam-details-physical-standards">
                <div className="exam-details-physical-category">
                  <h4>{language === 'mr' ? 'पुरुष उमेदवारांसाठी:' : 'For Male Candidates:'}</h4>
                  <ul>
                    <li><strong>{language === 'mr' ? 'उंची:' : 'Height:'}</strong> {examData.eligibilityCriteria.physical.male.height}</li>
                    <li><strong>{language === 'mr' ? 'छाती:' : 'Chest:'}</strong> {examData.eligibilityCriteria.physical.male.chest}</li>
                  </ul>
                </div>
                <div className="exam-details-physical-category">
                  <h4>{language === 'mr' ? 'महिला उमेदवारांसाठी:' : 'For Female Candidates:'}</h4>
                  <ul>
                    <li><strong>{language === 'mr' ? 'उंची:' : 'Height:'}</strong> {examData.eligibilityCriteria.physical.female.height}</li>
                  </ul>
                </div>
              </div>
            </div>}

          </div>
        )}
      </div>

      {/* Related Exams Section */}
      <div className="exam-details-related">
        <h2>{language === 'mr' ? 'इतर तत्सम परीक्षा' : 'Similar Exams'}</h2>
        <div className="exam-details-related-grid">
          {examData.relatedExams.map((exam, index) => (
            <div className="exam-details-related-card" key={index}>
              <h3>{exam.title}</h3>
              <p>{exam.body}</p>
              <Link to={`/exams-details/${exam.slug}`}>{language === 'mr' ? 'सविस्तर माहिती पहा' : 'View Details'}</Link>
            </div>
          ))}
        </div>
      </div>



    </div>
  );
};

export default ExamDetails;